import React, { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { Button } from '@/components/ui/therapist/Button';
import { Modal } from '@/components/ui/therapist/Modal';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { SetType } from '@/components/Template/types';
import { Text } from '@/components/Text';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';

import { styles } from './styles';

type SetStackParamList = {
  WordSet: { selectedID: string; templateMode?: 'create' };
  SentenceSet: { selectedID: string; templateMode?: 'create' };
  TextSet: { selectedID: string; templateMode?: 'create' };
};

const setsNavigation = {
  wordsets: { title: 'Wörterset', navigation: 'WordSet' },
  sentencesets: { title: 'Sätzeset', navigation: 'SentenceSet' },
  textsets: { title: 'Texteset', navigation: 'TextSet' },
} satisfies Record<
  SetType,
  { title: string; navigation: keyof SetStackParamList }
>;

type SetsModalProps = {
  changeSetsModalVisibility: (visible: boolean) => void;
  createAsTemplate?: boolean;
  isSetsModalVisible: boolean;
};

export function SetsModal({
  changeSetsModalVisibility,
  createAsTemplate = false,
  isSetsModalVisible,
}: SetsModalProps) {
  const sets = useMemo(() => {
    return Object.entries(setsNavigation).filter(([key]) =>
      createAsTemplate ? key !== 'textsets' : true,
    );
  }, [setsNavigation]);
  const [selectedSet, setSelectedSet] = useState<SetType>('wordsets');
  const { navigate } = useNavigation<StackNavigationProp<SetStackParamList>>();
  const { selectedID } = useDashboardContext();

  const handleCancelPress = useCallback(() => {
    changeSetsModalVisibility(false);
  }, []);

  const handleNextPress = useCallback(() => {
    const { navigation } = setsNavigation[selectedSet];
    handleCancelPress();
    navigate(navigation, {
      selectedID,
      templateMode: createAsTemplate ? 'create' : undefined,
    });
  }, [selectedSet, selectedID]);

  const handleSelectedSetChange = useCallback((value: SetType) => {
    return () => {
      if (!value) {
        return;
      }
      setSelectedSet(value);
    };
  }, []);

  return (
    <Modal
      isVisible={isSetsModalVisible}
      onClose={handleCancelPress}
      title={
        createAsTemplate
          ? 'Möchten Sie ein Wörter- oder Sätzeset als neue Vorlage erstellen?'
          : 'Möchten Sie ein Wörter-, Sätze- oder Texteset erstellen?'
      }
      confirmActionButton={<Button label="Weiter" onPress={handleNextPress} />}
      cancelActionButton={
        <Button
          label="Abbrechen"
          onPress={handleCancelPress}
          look="secondary"
        />
      }
    >
      <View style={styles.setsContainer}>
        {sets.map(([key, set], index) => {
          const typedKey = key as SetType;
          const notTheLast = index !== sets.length - 1;
          const isSelected = selectedSet === typedKey;
          return (
            <React.Fragment key={typedKey}>
              <PressableNeolexon
                onPress={handleSelectedSetChange(typedKey)}
                key={typedKey}
              >
                <View
                  style={[styles.setItem, isSelected && styles.selectedSetItem]}
                >
                  <Text style={styles.setItemText}>{set.title}</Text>
                  <View style={styles.radio}>
                    {isSelected ? <View style={styles.radioActive} /> : null}
                  </View>
                </View>
              </PressableNeolexon>
              <View
                style={[styles.separator, notTheLast && styles.borderBottom]}
              />
            </React.Fragment>
          );
        })}
      </View>
    </Modal>
  );
}
